<template>
  <Card class="decision-tree-viewer-slide-container" :class="modifierClassnames">
    <component :is="currentNodeView" :node="currentNode" :decision-tree="decisionTree" />
    <template #actions-start>
      <sp-button
        v-if="!decisionTree.isFirstStepNode"
        class="decision-tree-viewer-slide__nav-prev"
        variant="outlined"
        color="primary"
        :size="size"
        @click="goToPrevNode"
        :disabled="!decisionTree.prevNode"
      >
        {{ $t("buttons.prev") }}
      </sp-button>
    </template>

    <template #actions-end>
      <sp-button
        v-if="decisionTree.hasPossibleNextNodes"
        class="decision-tree-viewer-slide__nav-next"
        color="primary"
        :size="size"
        @click="goToNextNode"
        :disabled="!decisionTree.nextNode"
      >
        {{ $t("buttons.next") }}
      </sp-button>
    </template>
  </Card>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import Card from "./Card.vue";

const props = defineProps({
  modelValue: {
    type: String,
    required: true,
  },
  decisionTree: {
    type: Object,
    required: true,
  },
  currentNode: {
    type: Object,
    required: true,
  },
  currentNodeView: {
    type: Object,
    required: true,
  },
});

const TRANSITION_SUFFIX = { prev: "prev", next: "next" };
const emit = defineEmits(["update:modelValue", "next"]);

const transitionName = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

function setTransitionName(dirSuffix) {
  transitionName.value = `slide-${dirSuffix}`;
}

function pushState() {
  if (props.decisionTree.isAllowBooking) {
    return;
  }

  history.pushState({ namespace: "decision-tree-nav", currentNodeId: props.decisionTree.currentNode.id }, null);
}

function goToPrevNode() {
  setTransitionName(TRANSITION_SUFFIX.prev);
  props.decisionTree.goToPrevNode();
  pushState();
}

function goToNextNode() {
  setTransitionName(TRANSITION_SUFFIX.next);
  props.decisionTree.goToNextNode();
  pushState();
}

const modifierClassnames = computed(() => ({
  "--is-first": props.decisionTree.isFirstStepNode,
  "--is-last": props.decisionTree.isLastStepNode,
}));

const size = ref("default");
function setSize() {
  const cpSize = getComputedStyle(document.documentElement).getPropertyValue("--sp-dtv-card-action-size");
  const validatorSizes = ["small", "medium", "large", "default"];

  if (validatorSizes.includes(cpSize)) {
    size.value = cpSize;
  }
}

onMounted(() => {
  setSize();
});
</script>

<style lang="scss" scoped>
.decision-tree-viewer-slide-container {
  --slide-gap: var(--sp-dtv-slide-gap, 2rem);
  --slide-content-opacity: 1;
  --animation-time: var(--sp-dtv-slide-animation-time, 450ms);
  position: relative;

  &::after,
  &::before {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    width: 100%;
    border: var(--card-border);
    border-radius: var(--card-border-radius);
    box-shadow: var(--card-box-shadow);
    opacity: 1;
    transition: opacity var(--animation-time) ease-in-out;
  }

  &::after {
    left: calc(100% + var(--slide-gap));
  }

  &::before {
    right: calc(100% + var(--slide-gap));
  }

  &.--is-first::before,
  &.--is-last::after {
    opacity: 0;
  }

  &:deep(.dtv-card) {
    > * {
      opacity: var(--slide-content-opacity);
      transition: opacity var(--animation-time) ease-in-out;
    }
  }
}

.decision-tree-viewer-slide__nav-next,
.decision-tree-viewer-slide__nav-prev {
  --button-min-width: var(--dtv-slide-nav-btn-min-width, 9rem);
  margin-top: var(--dtv-slide-nav-btn-margin-top, 0.5rem);
}

.slide-prev-leave-to,
.slide-next-enter-from {
  transform: translateX(100%);
}

.slide-prev-enter-to,
.slide-next-enter-to {
  transform: translateX(0%);
  --slide-content-opacity: 1;
}

.slide-prev-enter-from,
.slide-next-leave-to {
  transform: translateX(-100%);
}

.slide-prev-enter-from,
.slide-next-leave-to,
.slide-next-enter-from {
  --slide-content-opacity: 0;
}

.slide-prev-enter-active,
.slide-prev-leave-active,
.slide-next-enter-active,
.slide-next-leave-active {
  transition: all var(--animation-time) ease-in-out;
  position: absolute;
}

.slide-prev-leave-active,
.slide-next-enter-active {
  &::before {
    display: none;
  }
}
.slide-prev-enter-active,
.slide-next-leave-active {
  &::after {
    display: none;
  }
}

.slide-prev-leave-active,
.slide-next-leave-active {
  --slide-content-opacity: 0;
}
</style>
